(() => {
  'use strict';

  const parseJson = (element, fallback) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('[Pure Saint] JSON konnte nicht gelesen werden.', error);
      return fallback;
    }
  };

  const normalize = (value) => String(value ?? '').trim().toLocaleLowerCase('de-DE');

  // glTF material factors use linear light, while Shopify swatches use sRGB.
  const hexToRgba = (hex, alpha = 1) => {
    const match = /^#([0-9a-f]{6})$/i.exec(hex || '#F4F2ED');
    const value = parseInt(match ? match[1] : 'F4F2ED', 16);
    const linear = (byte) => {
      const channel = byte / 255;
      return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    };
    return [linear((value >> 16) & 255), linear((value >> 8) & 255), linear(value & 255), alpha];
  };

  const hasHandle = (config) => {
    const value = normalize(config.handleVariantValue || config.handle);
    return value.startsWith('mit ') && !value.includes('ohne');
  };

  const escapeCanvasText = (value) =>
    String(value ?? '')
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .trim()
      .slice(0, 32);

  class PureSaint3DEngraver {
    static MATERIALS = {
      top: 'PS_Engraving_Top_Material',
      bottom: 'PS_Engraving_Bottom_Material',
      center: 'PS_Engraving_Center_Material',
      body: 'PS_Bottle_Material',
      cap: 'PS_Cap_Material',
      loop: 'PS_Loop_Material'
    };

    constructor(root, onReady) {
      this.root = root;
      this.modelViewer = root.querySelector('[data-ps-native-model] model-viewer');
      this.status = root.querySelector('[data-ps-render-status]');
      this.onReady = onReady;
      this.ready = false;
      this.modelReady = false;
      this.failed = false;
      this.pendingConfig = null;
      this.renderTimer = null;
      this.loadTimer = null;
      this.materials = {};
      this.surfaces = {};
      this.epoch = 0;
      this.rendering = false;
      this.appliedSignature = null;
      this.disposed = false;
      this.appearanceFrame = null;
      this.appearanceColor = null;
      this.appearanceTarget = null;
      this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.initialize();
    }

    initialize() {
      if (!this.modelViewer) {
        this.setStatus('Produktansicht · Personalisierung wird gespeichert', 'fallback');
        return;
      }
      this.root.classList.add('psp-has-native-model', 'psp-3d-loading');
      this.setStatus('3D-Vorschau wird geladen …', 'loading');
      this.handleLoad = () => { this.prepareModel(); };
      this.handleError = () => {
        this.epoch += 1;
        this.modelReady = false;
        this.boundModel = null;
        this.fail(new Error('Das 3D-Modell konnte nicht geladen werden.'));
      };
      this.modelViewer.addEventListener('load', this.handleLoad);
      this.modelViewer.addEventListener('error', this.handleError);
      this.loadTimer = window.setTimeout(() => {
        if (!this.modelReady) this.fail(new Error('Zeitüberschreitung beim Laden des 3D-Modells.'));
      }, 25000);

      // Register listeners before changing src: cached GLBs can load immediately.
      const url = this.root.dataset.psEngravingModelUrl;
      const sourceChanged = Boolean(url && this.modelViewer.getAttribute('src') !== url);
      this.modelViewer.removeAttribute('ios-src');
      if (sourceChanged) this.modelViewer.setAttribute('src', url);
      if (!sourceChanged && this.modelViewer.model) this.prepareModel();
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        this.modelViewer.removeAttribute('auto-rotate');
      }
    }

    async prepareModel() {
      const model = this.modelViewer.model;
      if (this.disposed || !model || this.boundModel === model) return;
      this.boundModel = model;
      const epoch = ++this.epoch;
      this.ready = false;
      this.modelReady = false;
      this.failed = false;
      this.appliedSignature = null;
      this.surfaces = {};
      window.cancelAnimationFrame(this.appearanceFrame);
      this.appearanceColor = null;
      this.appearanceTarget = null;
      window.clearTimeout(this.loadTimer);
      this.root.classList.remove('psp-3d-ready', 'psp-3d-fallback');
      this.root.classList.add('psp-3d-loading');
      this.setStatus('Deine 3D-Vorschau wird vorbereitet …', 'loading');

      try {
        Object.entries(PureSaint3DEngraver.MATERIALS).forEach(([key, name]) => {
          this.materials[key] = model.getMaterialByName(name);
        });
        if (!this.materials.body || !this.materials.cap || !this.materials.loop) {
          throw new Error('Die Flaschenmaterialien fehlen im 3D-Modell.');
        }
        await this.withTimeout(Promise.all(
          [...new Set(Object.values(this.materials).filter(Boolean))].map((material) => material.ensureLoaded?.())
        ));
        if (epoch !== this.epoch || this.disposed) return;

        // Color and the real handle work independently of the engraving texture.
        this.modelReady = true;
        this.materials.loop.setAlphaMode('MASK');
        this.materials.loop.setAlphaCutoff(0.5);
        if (this.pendingConfig) this.applyAppearance(this.pendingConfig);
        if (!this.materials.top || !this.materials.bottom) {
          throw new Error('Die gekrümmten Personalisierungsflächen fehlen.');
        }
        await Promise.all(['top', 'bottom', 'center'].map((key) =>
          this.createSurface(key, this.materials[key], epoch)
        ));
        if (epoch !== this.epoch || this.disposed) return;

        this.ready = true;
        this.root.classList.remove('psp-3d-loading', 'psp-3d-fallback');
        this.root.classList.add('psp-3d-ready');
        this.setStatus('3D-Vorschau bereit', 'ready');
        this.onReady?.();
        if (this.pendingConfig) this.queue(this.pendingConfig, true);
        this.root.dispatchEvent(new CustomEvent('pure-saint:engraving-ready', {
          bubbles: true,
          detail: { modelViewer: this.modelViewer }
        }));
      } catch (error) {
        if (epoch === this.epoch && !this.disposed) this.fail(error);
      }
    }

    async withTimeout(promise) {
      let timer;
      try {
        return await Promise.race([
          promise,
          new Promise((_, reject) => {
            timer = window.setTimeout(() => reject(new Error('3D-Textur konnte nicht geladen werden.')), 12000);
          })
        ]);
      } finally {
        window.clearTimeout(timer);
      }
    }

    async createSurface(key, material, epoch) {
      if (!material) return;
      material.setAlphaMode('BLEND');
      material.setDoubleSided(false);
      material.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 0]);
      const canvas = document.createElement('canvas');
      canvas.width = 2048;
      canvas.height = 1024;
      const context = canvas.getContext('2d', { alpha: true });
      if (!context) throw new Error('Die Textvorschau ist auf diesem Gerät nicht verfügbar.');

      // Shopify ships model-viewer 1.12. createTexture expects a URL, not a canvas.
      // Each curved patch gets its own texture; setURI reuses it without GPU leaks.
      const blank = canvas.toDataURL('image/png');
      const textures = await this.withTimeout(Promise.all([
        this.modelViewer.createTexture(blank), this.modelViewer.createTexture(blank)
      ]));
      if (epoch !== this.epoch || this.disposed) return;
      if (textures.some((texture) => !texture || typeof texture.source?.setURI !== 'function')) {
        throw new Error('Die Texturaktualisierung wird nicht unterstützt.');
      }
      textures.forEach((texture) => {
        texture.sampler.setWrapS(33071);
        texture.sampler.setWrapT(33071);
      });
      material.pbrMetallicRoughness.baseColorTexture.setTexture(textures[0]);
      this.surfaces[key] = { canvas, context, textures, active: 0, material, signature: null };
    }

    signature(config) {
      return JSON.stringify([
        config.text, config.initials, config.font, config.textColor,
        config.position, config.technique, config.hasLogo,
        config.hasLogo ? this.root.querySelector('[data-ps-logo-preview]')?.getAttribute('src') : ''
      ]);
    }

    queue(config, immediate = false) {
      this.pendingConfig = { ...config };
      if (this.disposed) return;
      if (this.modelReady) this.applyAppearance(config);
      if (!this.ready || this.signature(config) === this.appliedSignature) return;
      const logo = this.root.querySelector('[data-ps-logo-preview]');
      if (!config.text && !config.initials && !(config.hasLogo && logo?.complete && logo.naturalWidth > 0)) {
        this.hideSurfaces();
      }
      if (this.renderTimer !== null) return;
      this.renderTimer = window.requestAnimationFrame(() => {
        this.renderTimer = null;
        this.render();
      });
    }

    hideSurfaces() {
      Object.values(this.surfaces).forEach((surface) => {
        surface.material.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 0]);
      });
    }

    async render() {
      if (!this.ready || !this.pendingConfig || this.rendering || this.disposed) return;
      this.rendering = true;
      const epoch = this.epoch;
      try {
        // Serialize image loads. Rapid typing can never let an old result win.
        while (this.ready && !this.disposed && epoch === this.epoch) {
          const config = this.pendingConfig;
          const signature = this.signature(config);
          const surface = this.surfaces[config.position === 'Unten' ? 'bottom' : 'top'];
          if (signature === this.appliedSignature) break;
          const logo = this.root.querySelector('[data-ps-logo-preview]');
          const hasContent = Boolean(config.text || config.initials ||
            (config.hasLogo && logo?.complete && logo.naturalWidth > 0));
          if (hasContent && surface.signature !== signature) {
            surface.context.clearRect(0, 0, surface.canvas.width, surface.canvas.height);
            this.drawConfiguration(surface.context, config);
            const next = 1 - surface.active;
            const blob = await this.withTimeout(new Promise((resolve, reject) => {
              surface.canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Textvorschau konnte nicht erstellt werden.')), 'image/png');
            }));
            const url = URL.createObjectURL(blob);
            try {
              await this.withTimeout(surface.textures[next].source.setURI(url));
            } finally {
              URL.revokeObjectURL(url);
            }
            if (epoch !== this.epoch || this.disposed) return;
            if (signature !== this.signature(this.pendingConfig)) continue;
            surface.material.pbrMetallicRoughness.baseColorTexture.setTexture(surface.textures[next]);
            surface.active = next;
            surface.signature = signature;
          }
          if (signature !== this.signature(this.pendingConfig)) continue;
          this.hideSurfaces();
          const engraved = normalize(config.technique).includes('gravur');
          surface.material.pbrMetallicRoughness.setMetallicFactor(engraved ? 0.82 : 0.04);
          surface.material.pbrMetallicRoughness.setRoughnessFactor(engraved ? 0.34 : 0.68);
          surface.material.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, hasContent ? 1 : 0]);
          this.appliedSignature = signature;
          this.setStatus('3D-Vorschau bereit', 'ready');
        }
      } catch (error) {
        if (epoch === this.epoch && !this.disposed) this.fail(error);
      } finally {
        this.rendering = false;
        if (this.ready && !this.disposed && this.signature(this.pendingConfig) !== this.appliedSignature) {
          this.queue(this.pendingConfig, true);
        }
      }
    }

    applyAppearance(config) {
      const rgba = hexToRgba(config.bottleColorHex);
      this.materials.loop.pbrMetallicRoughness.setBaseColorFactor([
        ...(this.appearanceColor || rgba).slice(0, 3), hasHandle(config) ? 1 : 0
      ]);
      this.handleVisible = hasHandle(config);
      if (this.appearanceTarget?.every((value, index) => value === rgba[index])) return;
      this.appearanceTarget = rgba;
      window.cancelAnimationFrame(this.appearanceFrame);
      if (!this.appearanceColor || this.reducedMotion) {
        this.paintAppearance(rgba);
        return;
      }
      const from = [...this.appearanceColor];
      const start = performance.now();
      const animate = (now) => {
        if (this.disposed || !this.modelReady) return;
        const t = Math.min(1, (now - start) / 280);
        const eased = t * t * (3 - 2 * t);
        this.paintAppearance(from.map((value, index) => value + (rgba[index] - value) * eased));
        this.appearanceFrame = t < 1 ? window.requestAnimationFrame(animate) : null;
      };
      this.appearanceFrame = window.requestAnimationFrame(animate);
    }

    paintAppearance(rgba) {
      this.appearanceColor = [...rgba];
      [this.materials.body, this.materials.cap].forEach((material) => {
        material.pbrMetallicRoughness.setBaseColorFactor([...rgba]);
        material.pbrMetallicRoughness.setMetallicFactor(0.04);
        material.pbrMetallicRoughness.setRoughnessFactor(0.5);
      });
      this.materials.loop.pbrMetallicRoughness.setBaseColorFactor([...rgba.slice(0, 3), this.handleVisible ? 1 : 0]);
      this.materials.loop.pbrMetallicRoughness.setMetallicFactor(0.02);
      this.materials.loop.pbrMetallicRoughness.setRoughnessFactor(0.86);
    }

    showFront() {
      if (!this.modelViewer) return;
      this.modelViewer.removeAttribute('auto-rotate');
      const orbit = this.modelViewer.getCameraOrbit?.();
      const radius = Number.isFinite(orbit?.radius) ? `${orbit.radius}m` : '135%';
      this.modelViewer.setAttribute('camera-orbit', `0deg 84deg ${radius}`);
    }

    fail(error) {
      this.ready = false;
      this.failed = true;
      window.clearTimeout(this.loadTimer);
      this.hideSurfaces();
      this.root.classList.remove('psp-3d-loading', 'psp-3d-ready');
      this.root.classList.add('psp-3d-fallback');
      this.setStatus(this.modelReady
        ? 'Textvorschau nicht verfügbar. Farbe und Griff bleiben wählbar.'
        : '3D-Vorschau auf diesem Gerät nicht verfügbar.', 'fallback');
      this.onReady?.();
      console.warn('[Pure Saint] 3D-Vorschau:', error);
    }

    destroy() {
      this.disposed = true;
      this.epoch += 1;
      window.cancelAnimationFrame(this.renderTimer);
      window.cancelAnimationFrame(this.appearanceFrame);
      window.clearTimeout(this.loadTimer);
      this.modelViewer?.removeEventListener('load', this.handleLoad);
      this.modelViewer?.removeEventListener('error', this.handleError);
    }

    drawConfiguration(context, config) {
      const text = escapeCanvasText(config.text);
      const initials = escapeCanvasText(config.initials).toUpperCase();
      const logo = this.root.querySelector('[data-ps-logo-preview]');
      const hasLogo = Boolean(config.hasLogo && logo?.complete && logo.naturalWidth > 0);
      if (!text && !initials && !hasLogo) return;

      const isEngraving = normalize(config.technique).includes('gravur');
      const canvas = context.canvas;
      const content = [];

      context.save();
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      if (hasLogo) {
        const ratio = Math.min(620 / logo.naturalWidth, 240 / logo.naturalHeight, 1);
        const width = logo.naturalWidth * ratio;
        const height = logo.naturalHeight * ratio;
        context.save();
        context.globalAlpha = 0.94;
        context.filter = isEngraving
          ? 'grayscale(1) contrast(1.5) brightness(1.32)'
          : 'none';
        context.drawImage(logo, (canvas.width - width) / 2, 94, width, height);
        context.restore();
      }

      if (text) content.push({ value: text, kind: 'text' });
      if (initials) content.push({ value: initials, kind: 'initials' });

      const hasUpperGraphic = hasLogo;
      const centerY = hasUpperGraphic ? 650 : 512;
      const gap = content.length > 1 ? 178 : 0;

      content.forEach((item, index) => {
        const y = centerY + (index - (content.length - 1) / 2) * gap;
        const style = this.fontStyle(config.font, item.kind);
        const fittedSize = this.fitFont(context, item.value, style, item.kind === 'initials' ? 980 : 1650);
        context.font = this.fontDeclaration(style, fittedSize);

        const tracking = item.kind === 'initials'
          ? Math.max(20, fittedSize * 0.24)
          : (style.signature ? Math.max(1, fittedSize * 0.012) : Math.max(8, fittedSize * 0.075));

        this.drawPremiumText(context, item.value, canvas.width / 2, y, tracking, {
          engraving: isEngraving,
          color: config.textColor || '#171717'
        });
      });

      context.restore();
    }

    fontStyle(font, kind) {
      if (kind === 'initials') {
        return { family: 'Arial, Helvetica, sans-serif', weight: 600, italic: false, signature: false };
      }

      const styles = {
        'Editorial Serif': { family: 'Georgia, "Times New Roman", serif', weight: 500, italic: false, signature: false },
        'Minimal Light': { family: 'Arial, Helvetica, sans-serif', weight: 300, italic: false, signature: false },
        'Signature': { family: '"Brush Script MT", "Segoe Script", cursive', weight: 400, italic: false, signature: true },
        'Modern Sans': { family: 'Arial, Helvetica, sans-serif', weight: 600, italic: false, signature: false }
      };
      return styles[font] || styles['Modern Sans'];
    }

    fontDeclaration(style, size) {
      return `${style.italic ? 'italic ' : ''}${style.weight} ${size}px ${style.family}`;
    }

    fitFont(context, value, style, maxWidth) {
      let size = style.signature ? 230 : 190;
      const min = style.signature ? 88 : 72;
      while (size > min) {
        context.font = this.fontDeclaration(style, size);
        const tracking = style.signature ? size * 0.012 : size * 0.075;
        const width = context.measureText(value).width + Math.max(0, value.length - 1) * tracking;
        if (width <= maxWidth) break;
        size -= 4;
      }
      return size;
    }

    drawPremiumText(context, value, x, y, tracking, options) {
      // Keep the customer's spelling. Initials are uppercased in getConfig.
      const text = String(value);
      context.save();
      if (!options.engraving) {
        context.fillStyle = options.color;
        this.drawTrackedText(context, text, x, y, tracking, 'fill');
      } else {
        // Fine exposed-steel finish; model lighting supplies the reflections.
        // No drop shadow or thick outline that would make the letters float.
        const steel = context.createLinearGradient(0, y - 100, 0, y + 100);
        steel.addColorStop(0, '#dfe5e8');
        steel.addColorStop(0.52, '#a5adb3');
        steel.addColorStop(1, '#cbd1d5');
        context.fillStyle = steel;
        context.strokeStyle = 'rgba(57, 65, 70, .4)';
        context.lineWidth = 1.2;
        this.drawTrackedText(context, text, x, y, tracking, 'stroke');
        this.drawTrackedText(context, text, x, y, tracking, 'fill');
      }
      context.restore();
    }

    drawTrackedText(context, value, x, y, tracking, mode) {
      if (context.font.includes('Brush Script')) {
        if (mode === 'stroke') context.strokeText(value, x, y);
        else context.fillText(value, x, y);
        return;
      }
      const glyphs = Array.from(value);
      const widths = glyphs.map((glyph) => context.measureText(glyph).width);
      const totalWidth = widths.reduce((sum, width) => sum + width, 0) + Math.max(0, glyphs.length - 1) * tracking;
      let cursor = x - totalWidth / 2;

      glyphs.forEach((glyph, index) => {
        const glyphX = cursor + widths[index] / 2;
        if (mode === 'stroke') context.strokeText(glyph, glyphX, y);
        else context.fillText(glyph, glyphX, y);
        cursor += widths[index] + tracking;
      });
    }

    setStatus(message, state) {
      if (!this.status) return;
      this.status.dataset.state = state;
      const label = this.status.querySelector('[data-ps-render-label]');
      if (label) label.textContent = message;
    }
  }

  class PureSaintPersonalizer {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-ps-form]');
      this.variants = parseJson(root.querySelector('[data-ps-variants]'), []);
      this.optionNames = parseJson(root.querySelector('[data-ps-product-options]'), []);
      this.currency = root.dataset.currency || 'EUR';
      this.previewUrl = null;
      this.lastConfig = null;

      this.elements = {
        stage: root.querySelector('[data-ps-stage]'),
        panel: root.querySelector('[data-ps-config-panel]'),
        overlay: root.querySelector('[data-ps-overlay]'),
        previewImage: root.querySelector('[data-ps-bottle-preview-image]'),
        handlePreview: root.querySelector('[data-ps-handle-preview]'),
        stageBadge: root.querySelector('[data-ps-stage-badge]'),
        previewText: root.querySelector('[data-ps-preview-text]'),
        previewInitials: root.querySelector('[data-ps-preview-initials]'),
        logoPreview: root.querySelector('[data-ps-logo-preview]'),
        tint: root.querySelector('[data-ps-bottle-tint]'),
        price: root.querySelector('[data-ps-price]'),
        comparePrice: root.querySelector('[data-ps-compare-price]'),
        variantId: root.querySelector('[data-ps-variant-id]'),
        variantMessage: root.querySelector('[data-ps-variant-message]'),
        addButton: root.querySelector('[data-ps-add]'),
        addLabel: root.querySelector('[data-ps-add-label]'),
        configJson: root.querySelector('[data-ps-config-json]'),
        configId: root.querySelector('[data-ps-config-id]'),
        textCount: root.querySelector('[data-ps-text-count]'),
        colorOutput: root.querySelector('[data-ps-color-output]'),
        printColor: root.querySelector('[data-ps-print-color]'),
        uploadFilename: root.querySelector('[data-ps-upload-filename]'),
        uploadZone: root.querySelector('[data-ps-upload-zone]')
      };

      this.engraver = new PureSaint3DEngraver(root, () => {
        if (this.lastConfig) {
          this.updatePreview(this.lastConfig);
          this.updateMetadata(this.lastConfig);
        }
      });

      this.bind();
      this.update();
    }

    bind() {
      if (!this.form) return;

      this.form.addEventListener('focusin', (event) => {
        if (event.target.matches('[data-ps-field="text"], [data-ps-field="initials"]')) this.engraver.showFront();
      });
      this.form.addEventListener('input', () => this.update());
      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[data-ps-field="logo"]')) {
          this.handleLogo(event.target.files?.[0]);
        }
        if (event.target.matches('[data-ps-field="position"]')) this.engraver.showFront();
        this.update();
      });

      if (this.elements.logoPreview) {
        this.elements.logoPreview.addEventListener('load', () => {
          this.engraver.appliedSignature = null;
          this.update();
        });
      }

      this.root.querySelector('[data-ps-start]')?.addEventListener('click', () => {
        const firstField = this.form.querySelector('[data-ps-field="text"]');
        this.elements.panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => firstField?.focus({ preventScroll: true }), 420);
      });

      if (this.elements.uploadZone) {
        ['dragenter', 'dragover'].forEach((type) => {
          this.elements.uploadZone.addEventListener(type, () => this.elements.uploadZone.classList.add('is-dragging'));
        });
        ['dragleave', 'drop'].forEach((type) => {
          this.elements.uploadZone.addEventListener(type, () => this.elements.uploadZone.classList.remove('is-dragging'));
        });
      }

      this.handleUnload = (event) => {
        if (!event.target?.contains(this.root)) return;
        this.engraver.destroy();
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        document.removeEventListener('shopify:section:unload', this.handleUnload);
      };
      document.addEventListener('shopify:section:unload', this.handleUnload);
    }

    getField(selector) {
      return this.form?.querySelector(`[data-ps-field="${selector}"]`);
    }

    getCheckedField(selector) {
      return this.form?.querySelector(`[data-ps-field="${selector}"]:checked`);
    }

    getConfig() {
      const bottle = this.getCheckedField('bottleColor');
      const handle = this.getCheckedField('handle');
      const technique = this.getField('technique');
      const logo = this.getField('logo');
      const text = this.getField('text')?.value.trim() || '';
      const initials = this.getField('initials')?.value.trim().toUpperCase() || '';
      const font = this.getField('font')?.value || 'Modern Sans';
      const textColor = this.getField('textColor')?.value || '#171717';
      const position = this.getField('position')?.value || 'Oben';
      const techniqueOption = technique?.selectedOptions?.[0];
      const file = logo?.files?.[0];

      return {
        text,
        initials,
        font,
        textColor,
        bottleColor: bottle?.value || '',
        bottleColorHex: bottle?.dataset.psColor || '',
        bottleVariantValue: bottle?.dataset.psVariantValue || bottle?.value || '',
        bottleImage: bottle?.dataset.psImage || '',
        bottleImageAlt: bottle?.dataset.psImageAlt || '',
        motif: '',
        position,
        handle: handle?.value || '',
        handleVariantValue: handle?.dataset.psVariantValue || handle?.value || '',
        technique: technique?.value || '',
        techniqueVariantValue: techniqueOption?.dataset.psVariantValue || technique?.value || '',
        logoFileName: file?.name || '',
        hasLogo: Boolean(file),
        timestamp: new Date().toISOString()
      };
    }

    update() {
      if (!this.form) return;
      const config = this.getConfig();
      this.lastConfig = config;
      this.updatePreview(config);
      this.updateVariant(config);
      this.updateMetadata(config);
      this.dispatch(config);
    }

    updatePreview(config) {
      const normalizedHandle = normalize(config.handleVariantValue || config.handle);
      const hasHandle =
        normalizedHandle.includes('mit tragegriff') ||
        (normalizedHandle.startsWith('mit ') && !normalizedHandle.includes('ohne'));
      const isEngraving = normalize(config.technique).includes('gravur');

      this.root.classList.toggle('psp-has-handle', hasHandle);
      this.root.classList.toggle('psp-is-engraving', isEngraving);
      // The 2D hook icon is only a stand-in for the true 3D-Tragegriff. Whenever a
      // native model-viewer is present, the real GLB loop material owns the
      // handle toggle, so the flat icon must never float on top of it.
      const showHandlePreviewFallback = hasHandle && !this.engraver.modelViewer;
      this.elements.handlePreview?.classList.toggle('is-visible', showHandlePreviewFallback);
      if (this.elements.printColor) this.elements.printColor.hidden = isEngraving;

      if (this.elements.stageBadge) {
        const baseLabel = this.engraver.ready
          ? (isEngraving ? '360° · Live-Gravur' : '360° · Live-Druck')
          : (this.engraver.modelReady ? '360° · Flaschenvorschau' : 'Produktansicht');
        this.elements.stageBadge.textContent = hasHandle
          ? `${baseLabel} · Mit Tragegriff`
          : `${baseLabel} · Ohne Tragegriff`;
      }

      if (this.elements.previewImage && config.bottleImage) {
        if (this.elements.previewImage.getAttribute('src') !== config.bottleImage) {
          this.elements.previewImage.setAttribute('src', config.bottleImage);
        }
        this.elements.previewImage.setAttribute(
          'alt',
          config.bottleImageAlt || config.bottleColor || 'Pure Saint Flasche'
        );
      }

      if (this.elements.overlay) {
        this.elements.previewText.textContent = config.text;
        this.elements.previewInitials.textContent = config.initials;
        this.elements.overlay.dataset.font = config.font;
        this.elements.overlay.dataset.position =
          config.position === 'Unten' ? 'Vorne unten' : 'Vorne oben';
        this.elements.overlay.style.setProperty('--ps-preview-text-color', config.textColor);
        this.elements.overlay.classList.toggle(
          'is-empty',
          !(config.text || config.initials || config.hasLogo)
        );
      }

      this.engraver.queue(config);
      const help = this.root.querySelector('[data-ps-viewer-help]');
      if (help && this.engraver.modelViewer) {
        help.textContent = this.engraver.ready
          ? 'Drehe und zoome deine Flasche. Name, Initialen und Tragegriff sind Teil der 3D-Vorschau.'
          : (this.engraver.failed
            ? 'Deine Auswahl wird mit der Bestellung gespeichert. Die 3D-Personalisierung kann hier gerade nicht angezeigt werden.'
            : 'Deine 3D-Vorschau lädt. Du kannst deine Flasche bereits gestalten.');
      }

      if (this.elements.tint && config.bottleColorHex) {
        this.elements.tint.style.setProperty('--ps-bottle-preview-color', config.bottleColorHex);
      }

      if (this.elements.textCount) {
        this.elements.textCount.textContent = String(config.text.length);
      }

      if (this.elements.colorOutput) {
        this.elements.colorOutput.value = config.textColor.toUpperCase();
        this.elements.colorOutput.textContent = config.textColor.toUpperCase();
      }
    }

    handleLogo(file) {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }

      if (this.elements.uploadFilename) {
        this.elements.uploadFilename.textContent = file?.name || 'Keine Datei ausgewählt';
      }

      if (!this.elements.logoPreview) return;

      if (file && file.type.startsWith('image/')) {
        this.previewUrl = URL.createObjectURL(file);
        this.elements.logoPreview.src = this.previewUrl;
        this.elements.logoPreview.hidden = false;
      } else {
        this.elements.logoPreview.removeAttribute('src');
        this.elements.logoPreview.hidden = true;
      }
    }

    updateVariant(config) {
      if (!Array.isArray(this.variants) || this.variants.length === 0) return;

      const mappings = new Map();
      const addMapping = (optionName, value) => {
        if (optionName?.trim() && value?.trim()) mappings.set(normalize(optionName), value.trim());
      };

      addMapping(this.root.dataset.handleOptionName, config.handleVariantValue);
      addMapping(this.root.dataset.colorOptionName, config.bottleVariantValue);
      addMapping(this.root.dataset.techniqueOptionName, config.techniqueVariantValue);
      addMapping(
        this.root.dataset.logoOptionName,
        config.hasLogo ? this.root.dataset.logoYesValue : this.root.dataset.logoNoValue
      );

      const activeMappings = [...mappings.entries()].filter(([name]) =>
        this.optionNames.some((optionName) => normalize(optionName) === name)
      );

      let variant = null;
      if (activeMappings.length > 0) {
        variant = this.variants.find((candidate) => activeMappings.every(([normalizedName, selectedValue]) => {
          const index = this.optionNames.findIndex((optionName) => normalize(optionName) === normalizedName);
          return index >= 0 && normalize(candidate.options?.[index]) === normalize(selectedValue);
        }));
      }

      if (!variant) {
        const currentId = Number(this.elements.variantId?.value);
        variant = this.variants.find((candidate) => Number(candidate.id) === currentId) || this.variants[0];
      }

      if (this.elements.variantId) this.elements.variantId.value = variant.id;
      if (this.elements.price) this.elements.price.textContent = this.formatMoney(variant.price);

      if (this.elements.comparePrice) {
        const showCompare = Number(variant.compare_at_price) > Number(variant.price);
        this.elements.comparePrice.hidden = !showCompare;
        this.elements.comparePrice.textContent = showCompare ? this.formatMoney(variant.compare_at_price) : '';
      }

      const exactCombinationMissing = activeMappings.length > 0 && !this.variants.some((candidate) => activeMappings.every(([normalizedName, selectedValue]) => {
        const index = this.optionNames.findIndex((optionName) => normalize(optionName) === normalizedName);
        return index >= 0 && normalize(candidate.options?.[index]) === normalize(selectedValue);
      }));

      const canAdd = Boolean(variant.available) && !exactCombinationMissing;
      const availableLabel = this.elements.addButton?.dataset.psAvailableLabel || 'In den Warenkorb';
      if (this.elements.addButton) this.elements.addButton.disabled = !canAdd;
      if (this.elements.addLabel) {
        this.elements.addLabel.textContent = exactCombinationMissing
          ? 'Kombination prüfen'
          : (variant.available ? availableLabel : 'Derzeit nicht verfügbar');
      }

      if (this.elements.variantMessage) {
        this.elements.variantMessage.hidden = !exactCombinationMissing;
        this.elements.variantMessage.textContent = exactCombinationMissing
          ? 'Diese Kombination ist derzeit nicht verfügbar. Bitte wähle eine andere Farbe oder Ausführung.'
          : '';
      }
    }

    updateMetadata(config) {
      const variantId = this.elements.variantId?.value || '';
      const compactConfig = {
        t: config.text,
        i: config.initials,
        f: config.font,
        tc: config.textColor,
        bc: config.bottleColor,
        h: config.handle,
        m: config.motif,
        p: config.position,
        v: config.technique,
        l: config.logoFileName,
        r: this.engraver.ready ? '3d-material' : 'fallback',
        vid: variantId
      };

      const previewMode = this.form.querySelector('[name="properties[_Vorschau-Modus]"]');
      if (previewMode) previewMode.value = this.engraver.ready ? '3D-Materialvorschau' : 'Produktansicht ohne 3D-Personalisierung';
      if (this.elements.configJson) {
        this.elements.configJson.value = JSON.stringify(compactConfig);
      }

      if (this.elements.configId && !this.elements.configId.value) {
        const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase();
        this.elements.configId.value = `PS-${Date.now().toString(36).toUpperCase()}-${randomPart}`;
      }
    }

    dispatch(config) {
      const detail = {
        productId: this.root.dataset.productId,
        sectionId: this.root.dataset.sectionId,
        variantId: this.elements.variantId?.value || '',
        configuration: config,
        mount: this.root.querySelector('[data-ps-configurator-mount]'),
        modelViewer: this.engraver.modelViewer,
        materialEngravingReady: this.engraver.ready
      };

      this.root.dispatchEvent(new CustomEvent('pure-saint:configuration-change', { detail, bubbles: true }));
      window.dispatchEvent(new CustomEvent('pure-saint:configuration-change', { detail }));

      if (window.PureSaint3DAdapter && typeof window.PureSaint3DAdapter.update === 'function') {
        try {
          window.PureSaint3DAdapter.update(detail);
        } catch (error) {
          console.error('[Pure Saint] Fehler im externen 3D-Adapter.', error);
        }
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency: this.currency
        }).format(amount);
      } catch (_) {
        return `${amount.toFixed(2)} ${this.currency}`;
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ps-personalizer]').forEach((root) => {
      if (root.dataset.psInitialized === 'true') return;
      root.dataset.psInitialized = 'true';
      new PureSaintPersonalizer(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

